import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { WARRANTY_PERMISSION, hasWarrantyPermission } from "../common/warrantyAccess";
import WarrantyPage from "../common/WarrantyPage";
import { getHealth } from "../common/warrantyApi";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { PARTNER_SIGNALS, signalLabel, signalSentence } from "../common/opsLabels";
import { ExceptionTable } from "./ExceptionList";
import PartnerPolicyDetail from "./PartnerPolicyDetail";
import "../warranty.css";

// The six partner signals as tabs with their counts from Warranty health, each list drawn by the
// same table as the other exception lists. A 404 on a list reads "not switched on yet".
const PartnerPolicies = () => {
  const [params, setParams] = useSearchParams();
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasWarrantyPermission(selfInfo, WARRANTY_PERMISSION.OPS);
  useWarrantyNav(allowed);

  const tab = PARTNER_SIGNALS.includes(params.get("tab")) ? params.get("tab") : PARTNER_SIGNALS[0];
  const [counts, setCounts] = useState({ status: "loading", byKey: {}, message: "" });
  const [open, setOpen] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [queued, setQueued] = useState({});
  const tabRefs = useRef({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadCounts = useCallback(async () => {
    const result = await getHealth(accessToken, "24h");
    if (!mounted.current) {
      return;
    }
    if (result.ok) {
      const byKey = {};
      result.data.signals.forEach((signal) => {
        byKey[signal.signal] = signal;
      });
      setCounts({ status: "ready", byKey, message: "" });
    } else {
      setCounts({ status: "error", byKey: {}, message: result.message });
    }
  }, [accessToken]);

  useEffect(() => {
    if (allowed) {
      loadCounts();
    }
  }, [allowed, loadCounts]);

  const choose = (signal) => {
    setOpen(null);
    setParams({ tab: signal }, { replace: true });
  };

  const onKeyDown = (event, index) => {
    const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) {
      return;
    }
    event.preventDefault();
    const next = PARTNER_SIGNALS[(index + step + PARTNER_SIGNALS.length) % PARTNER_SIGNALS.length];
    choose(next);
    const node = tabRefs.current[next];
    if (node) {
      node.focus();
    }
  };

  const countText = (signal) => {
    const item = counts.byKey[signal];
    return item ? String(item.count) : "-";
  };

  const actionDone = (policy) => {
    // A settle queues nothing: the reloaded row shows what follows (gone, or Refund cover again).
    if (policy.action !== "settle_cover_refund") {
      setQueued((prev) => ({ ...prev, [policy.id]: true }));
    }
    setReloadToken((value) => value + 1);
    loadCounts();
  };

  return (
    <WarrantyPage
      title="Partner policies"
      sub="Theft and roadside cover that an insurance partner issues. Only the failures are listed."
      allowed={allowed}
      deniedTitle="You do not have access to partner policies"
      deniedText="Ask an admin to give you the Warranty Operations permission."
    >
      <div className="warranty-tabs" role="tablist" aria-label="Partner policy lists">
        {PARTNER_SIGNALS.map((signal, index) => (
          <button
            key={signal}
            ref={(node) => {
              tabRefs.current[signal] = node;
            }}
            type="button"
            role="tab"
            id={`warranty-tab-${signal}`}
            aria-selected={tab === signal}
            aria-controls="warranty-tab-panel"
            tabIndex={tab === signal ? 0 : -1}
            className={`warranty-tab${tab === signal ? " is-active" : ""}`}
            onClick={() => choose(signal)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {signalLabel(signal)} <span className="warranty-tab-count">{countText(signal)}</span>
          </button>
        ))}
      </div>
      {counts.status === "error" && (
        <p className="warranty-muted warranty-ocr-note">
          The counts could not be loaded: {counts.message}
        </p>
      )}
      <div id="warranty-tab-panel" role="tabpanel" aria-labelledby={`warranty-tab-${tab}`}>
        <p className="warranty-dialog-hint">{signalSentence(tab)}</p>
        <ExceptionTable
          key={tab}
          signal={tab}
          windowKey="24h"
          onlyNew={false}
          accessToken={accessToken}
          onOpenItem={(item) => setOpen(item)}
          externalQueued={queued}
          reloadToken={reloadToken}
          onChanged={loadCounts}
        />
      </div>
      {open && (
        <PartnerPolicyDetail
          key={open.id}
          policyId={open.id}
          accessToken={accessToken}
          onClose={() => setOpen(null)}
          onActionDone={actionDone}
        />
      )}
    </WarrantyPage>
  );
};

export default PartnerPolicies;
